import { useMemo, useRef, useState } from "react";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { enumRulePairLogicCondition, QRulePair } from "@coreapp/CoreApp/QRulePair";
import {
  enumChartRuleModifiers,
  useChartRuleEditorController,
  type HeaderCustomAction
} from "../../Utils/useChartRuleEditorController";
import { RulesEditor } from "../../Utils/RulesEditor";
import { useFilterController } from "../useFilterController";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { appstr } from "@coreapp/utils/appstr";
import type { SharedFilterDoc } from "../useSharedFilterController";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type FilterBlockEditorProps = {
  filterCtrl: ReturnType<typeof useFilterController>;
  sharedFilterDoc?: SharedFilterDoc | null;
  onStopEditingShared?: () => void;
};

export const FilterBlockEditor = (props: FilterBlockEditorProps) => {
  const { filterCtrl, sharedFilterDoc: sharedFilterDocProp, onStopEditingShared } = props;
  const { canShareFilters: canShare } = usePermissionCheck();
  const [showShareModal, setShowShareModal] = useState(false);
  const triggerValidationRef = useRef<() => boolean>(() => true);

  const editingBlock = filterCtrl.filterBlockState.filterBlock ?? new QFilterBlock();
  const canShareCurrentFilter = !!sharedFilterDocProp || filterCtrl.filterBlockState.index != null;

  const headerCustomAction = useMemo<HeaderCustomAction | undefined>(
    () =>
      canShare && canShareCurrentFilter
        ? {
            icon: "share-nodes",
            onClick: () => {
              if (triggerValidationRef.current()) setShowShareModal(true);
            },
            buttonProps: {
              modifier: "tertiary",
              tooltipProps: { message: appstr("Share filter") },
              dataTestId: chartModeTestId("filter", "editor-share-btn")
            }
          }
        : undefined,
    [canShare, canShareCurrentFilter]
  );

  const ctrl = sharedFilterDocProp
    ? { ...filterCtrl, handleStopEditing: onStopEditingShared ?? filterCtrl.handleStopEditing }
    : filterCtrl;

  const blockIndex = filterCtrl.filterBlockState.index;

  const editorCtrl = useChartRuleEditorController({
    entity: enumChartRuleModifiers.Filter,
    editingElement: editingBlock,
    enableBlock: (block: QFilterBlock) => block.SetEnabled(true),
    getRules: (block: QFilterBlock) => block.fFilterRuleList ?? [],
    ctrl,
    sharedFilterDoc: sharedFilterDocProp ?? undefined,
    headerCustomAction,
    onDelete: blockIndex != null ? () => filterCtrl.handleBlockRemove(blockIndex)() : undefined
  });
  triggerValidationRef.current = editorCtrl.triggerValidation;
  const { blockAction, setRuleEditStatus } = editorCtrl;

  const addRule = blockAction(
    (
      updatingBlock: QFilterBlock,
      connectsWithCondition: string,
      ruleCondition: string,
      index?: number
    ) => {
      const newRule = updatingBlock.AddEmptyRule(index);
      newRule.fCondition = ruleCondition;

      const currentRules = updatingBlock.GetRules();
      const ruleIndex = index ?? currentRules.length - 1;
      const prevRule = currentRules[ruleIndex - 1];
      if (prevRule) {
        prevRule.fCondition = connectsWithCondition;
      }

      setRuleEditStatus(prevStatus => {
        const newStatus = { ...prevStatus };
        newStatus[newRule.fUID] = true;
        return newStatus;
      });
    }
  );

  const removeRule = blockAction((updatingBlock: QFilterBlock, ruleIndex: number) => {
    const oldRuleCondition = updatingBlock.GetRules()[ruleIndex].fCondition;
    const prevRule = updatingBlock.GetRules()[ruleIndex - 1];

    if (
      prevRule?.fCondition === enumRulePairLogicCondition.or &&
      oldRuleCondition === enumRulePairLogicCondition.and
    ) {
      prevRule.fCondition = enumRulePairLogicCondition.and;
    }

    updatingBlock.RemoveRuleAtIndex(ruleIndex);
    if (updatingBlock.GetRules().length === 0) {
      updatingBlock.AddEmptyRule();
    }
  });

  const changeRule = blockAction(
    (updatingBlock: QFilterBlock, ruleIndex: number, rule: QRulePair) => {
      updatingBlock.SetRule(ruleIndex, rule);
    }
  );

  const clearAllRules = blockAction((updatingBlock: QFilterBlock) => {
    updatingBlock.SetRules([]);
    const newRule = updatingBlock.AddEmptyRule();
    setRuleEditStatus({
      [newRule.fUID]: true
    });
  });

  return (
    <>
      <RulesEditor {...editorCtrl} methods={{ clearAllRules, addRule, changeRule, removeRule }} />
      {canShare && canShareCurrentFilter && (
        <ShareFilterModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          filterType="filter"
          definition={editingBlock.OutputToJSON?.() || {}}
          filterName={editingBlock.GetName?.() || ""}
          filterRef={editingBlock}
          {...(sharedFilterDocProp
            ? {
                editMode: true,
                existingFilterId: sharedFilterDocProp._id,
                existingSharedWith: sharedFilterDocProp.sharedWith
              }
            : {})}
        />
      )}
    </>
  );
};
